import React from "react";
import axios from "axios";
import { Loader2, Search, ArrowDown, ArrowUp } from "lucide-react";
import { Input } from "@/components/ui/input";
import { PlayerCardDialog } from "@/components/PlayerCardDialog";
import { ARCHETYPES } from "@/components/ArchetypeBadge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLS = [
  { k: "games", l: "GP" }, { k: "goals", l: "G" }, { k: "assists", l: "A" }, { k: "points", l: "PTS" },
  { k: "plusMinus", l: "+/-" }, { k: "penaltyMinutes", l: "PIM" }, { k: "timeOnIcePerGame", l: "TOI/G", str: true },
  { k: "powerPlayGoals", l: "PPG" }, { k: "powerPlayAssists", l: "PPA" }, { k: "shotsTotal", l: "S" },
  { k: "shootingPct", l: "S%", dp: 1 }, { k: "shifts", l: "SHFT" }, { k: "gameWinningGoals", l: "GWG" },
  { k: "faceoffsWon", l: "FW" }, { k: "faceoffsLost", l: "FL" }, { k: "faceoffPercent", l: "FO%", dp: 1 },
  { k: "shootoutAttempts", l: "SOA" }, { k: "shootoutGoals", l: "SOG" }, { k: "shootoutShotPct", l: "SO%", dp: 1 },
  { k: "gpg", l: "G/GP", dp: 2 },
];
const POS = ["ALL", "C", "LW", "RW", "D"];
const PAGE = 50;

const toSec = (t) => { const [m, s] = String(t || "0:0").split(":").map(Number); return m * 60 + (s || 0); };
const fmtVal = (c, v) => (v == null ? "—" : c.str ? v : c.dp ? Number(v).toFixed(c.dp) : Math.round(v));

const Header = ({ c, sort, setSort }) => (
  <th
    data-testid={`skater-sort-${c.k}`}
    onClick={() => setSort((s) => ({ k: c.k, dir: s.k === c.k && s.dir === "desc" ? "asc" : "desc" }))}
    className={`cursor-pointer select-none whitespace-nowrap px-2 py-2 text-right transition-colors hover:text-cyan-300 ${sort.k === c.k ? "text-cyan-300" : ""}`}
  >
    <span className="inline-flex items-center gap-0.5">
      {c.l}{sort.k === c.k && (sort.dir === "desc" ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />)}
    </span>
  </th>
);

export default function SkaterStatsTab() {
  const [data, setData] = React.useState(null);
  const [err, setErr] = React.useState(false);
  const [q, setQ] = React.useState("");
  const [pos, setPos] = React.useState("ALL");
  const [team, setTeam] = React.useState("ALL");
  const [sort, setSort] = React.useState({ k: "points", dir: "desc" });
  const [limit, setLimit] = React.useState(PAGE);
  const [card, setCard] = React.useState(null);
  const [arch, setArch] = React.useState("ALL");

  const load = React.useCallback(async () => {
    setErr(false);
    try { setData((await axios.get(`${API}/skater-stats`)).data); } catch { setErr(true); }
  }, []);
  React.useEffect(() => { load(); const t = setInterval(load, 30 * 60 * 1000); return () => clearInterval(t); }, [load]);

  const teams = React.useMemo(() => ["ALL", ...[...new Set((data?.players || []).map((p) => p.team))].sort()], [data]);
  const rows = React.useMemo(() => {
    const col = COLS.find((c) => c.k === sort.k);
    const ql = q.trim().toLowerCase();
    const list = (data?.players || []).filter((p) =>
      (pos === "ALL" || p.pos === pos) && (team === "ALL" || p.team === team) && (arch === "ALL" || p.archetype === arch) && (!ql || p.name.toLowerCase().includes(ql)));
    const v = (p) => (col?.str ? toSec(p[sort.k]) : p[sort.k] ?? -Infinity);
    return list.sort((a, b) => (sort.dir === "desc" ? v(b) - v(a) : v(a) - v(b)) || (b.points || 0) - (a.points || 0));
  }, [data, q, pos, team, arch, sort]);

  if (err) return (
    <div className="py-24 text-center font-mono text-xs text-rose-400" data-testid="skater-stats-error">
      Skater stats unavailable
      <button onClick={load} data-testid="skater-stats-retry" className="ml-3 rounded bg-slate-800 px-3 py-1 text-slate-300 hover:bg-slate-700">Retry</button>
    </div>
  );
  if (!data) return (
    <div className="flex flex-col items-center py-24 text-slate-500" data-testid="skater-stats-loading">
      <Loader2 className="h-7 w-7 animate-spin text-cyan-500" />
      <p className="mt-3 font-mono text-[11px] uppercase tracking-widest">Pulling skater stats…</p>
    </div>
  );

  return (
    <div className="space-y-3" data-testid="skater-stats-tab">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-wider text-slate-500">
            <span data-testid="skater-match-count" className="text-cyan-400">{rows.length}</span> of {data.count} skaters
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-500" />
            <Input data-testid="skater-search" value={q} onChange={(e) => { setQ(e.target.value); setLimit(PAGE); }} placeholder="Search player" className="w-52 border-slate-800 bg-slate-900 pl-8 font-mono text-xs" />
          </div>
          <select data-testid="skater-team-filter" value={team} onChange={(e) => { setTeam(e.target.value); setLimit(PAGE); }} className="h-9 rounded-md border border-slate-800 bg-slate-900 px-2 font-mono text-xs text-slate-200">
            {teams.map((t) => <option key={t} value={t}>{t === "ALL" ? "All teams" : t}</option>)}
          </select>
          <select data-testid="skater-archetype-filter" value={arch} onChange={(e) => { setArch(e.target.value); setLimit(PAGE); }} className="h-9 rounded-md border border-slate-800 bg-slate-900 px-2 font-mono text-xs text-slate-200">
            <option value="ALL">All archetypes</option>
            {Object.entries(ARCHETYPES).map(([k, m]) => <option key={k} value={k}>{m.icon} {k}</option>)}
            <option value="NO ARCHETYPE">No archetype</option>
          </select>
          <div className="flex rounded-md border border-slate-800 bg-slate-900 p-0.5">
            {POS.map((p) => (
              <button key={p} data-testid={`skater-pos-${p}`} onClick={() => { setPos(p); setLimit(PAGE); }}
                className={`rounded px-2.5 py-1 font-mono text-[11px] font-bold transition-colors ${pos === p ? "bg-cyan-500/20 text-cyan-300" : "text-slate-500 hover:text-slate-300"}`}>{p}</button>
            ))}
          </div>
        </div>
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-800">
        <table className="w-full font-mono text-[11px]" data-testid="skater-stats-table">
          <thead className="sticky top-0 bg-slate-900 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-2 py-2 text-left">RK</th>
              <th className="sticky left-0 z-10 bg-slate-900 px-2 py-2 text-left">Name</th>
              <th className="px-2 py-2 text-left">Team</th>
              <th className="px-2 py-2 text-left">POS</th>
              <th className="px-2 py-2 text-left">Archetype · L5</th>
              {COLS.map((c) => <Header key={c.k} c={c} sort={sort} setSort={setSort} />)}
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, limit).map((p, i) => (
              <tr key={p.id} className="border-t border-slate-800/60 transition-colors hover:bg-cyan-950/20" data-testid={`skater-row-${p.id}`}>
                <td className="px-2 py-1.5 text-slate-500">{i + 1}</td>
                <td className="sticky left-0 z-10 whitespace-nowrap bg-slate-950 px-2 py-1.5">
                  <button data-testid={`skater-name-${p.id}`} onClick={() => setCard(p)} className="font-bold text-slate-100 underline-offset-2 transition-colors hover:text-cyan-300 hover:underline">{p.name}</button>
                </td>
                <td className="px-2 py-1.5 text-slate-400">{p.team}</td>
                <td className="px-2 py-1.5 text-slate-400">{p.pos}</td>
                <td className="whitespace-nowrap px-2 py-1.5" data-testid={`skater-arch-${p.id}`} title={ARCHETYPES[p.archetype] ? `${ARCHETYPES[p.archetype].rule}\n${ARCHETYPES[p.archetype].support}` : ""}>
                  {ARCHETYPES[p.archetype] ? (
                    <span className="text-fuchsia-300">{ARCHETYPES[p.archetype].icon} {p.archetype}</span>
                  ) : <span className="text-slate-600">{p.archetype === "UNVERIFIED" ? "No L5 data" : "—"}</span>}
                  {p.l5 && <span className="ml-1.5 text-[9px] text-slate-500">{p.l5.g}G {p.l5.a}A {p.l5.sog}S</span>}
                  {p.tonight && <span className="ml-1.5 rounded bg-rose-950/60 px-1 text-[8px] font-bold text-rose-300">TONIGHT</span>}
                </td>
                {COLS.map((c) => (
                  <td key={c.k} className={`px-2 py-1.5 text-right ${sort.k === c.k ? "font-bold text-cyan-300" : "text-slate-300"}`}>{fmtVal(c, p[c.k])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > limit && (
        <button data-testid="skater-load-more" onClick={() => setLimit((l) => l + PAGE)} className="w-full rounded-lg border border-slate-800 bg-slate-900/60 py-2 font-mono text-[11px] uppercase tracking-wider text-slate-400 transition-colors hover:text-cyan-300">
          Show more ({rows.length - limit} left)
        </button>
      )}
      <PlayerCardDialog player={card} onClose={() => setCard(null)} />
      {!rows.length && <p className="py-6 text-center font-mono text-[11px] text-slate-600" data-testid="skater-empty">No skaters match</p>}
    </div>
  );
}
